import { StandardProps } from "../componentMap";
import { ProductDescriptionSideDrawerUI } from "./ProductDescriptionSideDrawer/ProductDescriptionSideDrawerUI";
import { ProductSpecificationSideDrawerUI } from "./ProductSpecificationSideDrawer/ProductSpecificationSideDrawerUI";
import { IngredientsSideDrawerUI } from "./IngredientsSideDrawer/IngredientsSideDrawerUI";
import { ProductDeliveryOptionsSideDrawerUI } from "./ProductDeliveryOptionsSideDrawer/ProductDeliveryOptionsSideDrawerUI";
import { useVariant } from "@/hooks/useVariant/useVariant";

import { getDescription } from "@/utils/product/getDescription";
import { sortBy } from "@/utils/sortBy/sortBy";
import { isAvailableToPreOrder } from "@/utils/variant/isAvailableToPreOrder";
import {
  Attribute,
  AvailableOption,
  UnavailableCollectionOption,
} from "@/types/prodcatBaseTypes";
import { getDeliveryPrice } from "../ProductAccordion/components/ProductDeliveryOptionsAccordion/util/getDeliveryPrice";

import { ensureIsActive } from "@/utils/product/ensureIsActive";
import { isInStoreOnly } from "@/utils/variant/isInStoreOnly";

import { getSizeAndFitAttributes } from "../ProductAccordion/components/ProductSizeAndFitAccordion/sizeAndFitUtils";
import { isOutlet } from "@/utils/variant/isOutlet";
import { isPerMetre } from "@/utils/product/isPerMetre";
import { HowToUseSideDrawerUI } from "./HowToUseSideDrawer/HowToUseSideDrawerUI";
import { GoodToKnow } from "@/components/ProductSideDrawers/GoodToKnow/GoodToKnow";
import { getEan } from "@/utils/variant/getEan";
import {
  DrawerConfiguration,
  productDrawerConfigSchema,
} from "./config/productDrawerConfigSchema";

/*
type DrawerConfiguration = {
  name:
    | "brand"
    | "buyingGuides"
    | "deliveryOptions"
    | "description"
    | "goodToKnow"
    | "howToUse"
    | "ingredients"
    | "outlet"
    | "returnsAndExchange"
    | "reviews"
    | "sizeAndFit"
    | "specialOffers"
    | "specification";
};
*/
const defaultDrawers: DrawerConfiguration = [
  {
    name: "description",
  },
  {
    name: "specification",
  },
  {
    name: "brand",
  },
  {
    name: "deliveryOptions",
  },
  {
    name: "reviews",
  },
];

const transformAvailable = (option: AvailableOption) => ({
  headline: option.headline,
  description: option.description,
  displayPrice: getDeliveryPrice(option),
});

const transformUnavailableCollection = (
  option: UnavailableCollectionOption,
) => ({
  headline: option.headline,
  description: option.description,
  displayPrice: "",
});

const validateConfigFromProps = (
  configuration: unknown,
): DrawerConfiguration | null => {
  const parsedConfig = productDrawerConfigSchema.safeParse(configuration);
  if (parsedConfig.success) {
    return parsedConfig.data;
  }
  return null;
};

export function ProductSideDrawers({
  product,
  runtimeConfig,
  configProps,
  flags,
}: Pick<StandardProps, "product" | "runtimeConfig" | "configProps" | "flags">) {
  const { variant } = useVariant(product);
  const drawers =
    validateConfigFromProps(configProps?.drawerConfig) || defaultDrawers;

  const showOutletReturnMessage = isOutlet(variant);
  const isRetired = !ensureIsActive(product);
  const isMeteredProduct = isPerMetre(product);

  const [endorsement] =
    variant.messaging?.filter((message) => message.type === "endorsement") ||
    [];

  const variantAttributes = variant.attributes || [];
  const primaryAttributes = product.attributes || [];
  const allAttributes = [...primaryAttributes, ...variantAttributes];
  const sortedAttributes = sortBy(allAttributes, (a) => a.displayName || "");

  const available = product.fulfilmentOptions?.available || [];
  const unavailableCollection =
    product.fulfilmentOptions?.unavailableCollection || [];

  const deliveryOptions = [
    ...available.map(transformAvailable),
    ...unavailableCollection.map(transformUnavailableCollection),
  ];

  let productSpecificationAttributes = sortedAttributes;
  let sizeAndFitAttributes: Attribute[] = [];

  if (drawers.some((da) => da.name === "sizeAndFit")) {
    const categoryIds = product?.browsePageHierarchy?.map(
      (bph) => bph.categoryId,
    );
    sizeAndFitAttributes = getSizeAndFitAttributes(
      categoryIds,
      sortedAttributes,
    );

    if (sizeAndFitAttributes?.length > 0) {
      productSpecificationAttributes = sortedAttributes.filter(
        (attr) => !sizeAndFitAttributes.includes(attr),
      );
    }
  }

  return (
    <div data-testid="product:sidedrawers" data-component="ProductSideDrawers">
      {drawers.map((drawer) => {
        switch (drawer.name) {
          case "description":
            return (
              <ProductDescriptionSideDrawerUI
                productType={product.__typename}
                displayCode={variant.displayCode}
                description={getDescription(product)}
                icons={product.icons || []}
                links={product.links || []}
                endorsement={endorsement}
                hideDescriptionLinks={
                  drawers.find((a) => a.name === "buyingGuides") !== undefined
                }
                showBeautySocietyLink={configProps?.beautySocietyLink === true}
                key={drawer.name}
              />
            );
          case "specification":
            return (
              <ProductSpecificationSideDrawerUI
                key={drawer.name}
                attributes={productSpecificationAttributes}
                productType={product.__typename}
              />
            );
          case "ingredients":
            return (
              <IngredientsSideDrawerUI
                drawerHeading="Ingredients"
                product={product}
                runtimeConfig={runtimeConfig}
                key={drawer.name}
              />
            );
          case "deliveryOptions":
            return (
              <ProductDeliveryOptionsSideDrawerUI
                key={drawer.name}
                isBladedArticle={
                  variant.sellingRestrictions?.age?.bladedArticle ?? false
                }
                deliveryOptions={deliveryOptions}
                skuId={variant.aliases.skuId}
                isAvailableToOrderOrPreorder={
                  variant.availability?.availableToOrder ||
                  isAvailableToPreOrder(variant)
                }
                stockApiBaseUrl={runtimeConfig.stockApiBaseUrl}
                stockApiKey={runtimeConfig.stockApiKey}
                showExtendedReturnsMessaging={
                  flags.showExtendedReturnsMessaging.value == true
                }
                extendedReturnsDates={{
                  startDate: String(flags.extendedReturnsPeriodStartDate.value),
                  endDate: String(flags.extendedReturnsPeriodEndDate.value),
                  deadlineDate: String(flags.extendedReturnsDeadlineDate.value),
                }}
                isInStoreOnly={isInStoreOnly(variant)}
                isRetired={isRetired}
                isMeteredProduct={isMeteredProduct}
                browsePageHierarchy={product.browsePageHierarchy}
                sellingProductType={product.sellingProductType}
                showOutletReturnMessage={showOutletReturnMessage}
                returnsStartDate="2024-09-26"
                returnsEndDate="2025-01-23"
                showChristmasDeliveryMessaging={
                  flags.showChristmasDeliveryMessaging.value == true
                }
              />
            );
          case "howToUse":
            return (
              <HowToUseSideDrawerUI
                key={drawer.name}
                howtoUseContent={product?.howToUse}
              />
            );
          case "goodToKnow":
            return (
              <GoodToKnow
                key={drawer.name}
                ean={getEan(variant)}
                isChanel={product?.brand?.name.toUpperCase() === "CHANEL"}
                cleanBeautyBaseUrl={runtimeConfig?.cleanBeautyBaseUrl}
              />
            );
          case "buyingGuides":
          case "outlet":
          case "brand":
          case "reviews":
          case "sizeAndFit":
          case "specialOffers":
          case "returnsAndExchange":
          default:
            return null;
        }
      })}
    </div>
  );
}
